import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function ClientPhotoUpdatePublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/public/client-photo-update/${token}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setState({ loading: false, error: e.detail || "This link is no longer active.", data: null });
        return;
      }
      setState({ loading: false, error: null, data: await res.json() });
    } catch {
      setState({ loading: false, error: "Could not load these photos.", data: null });
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const acknowledge = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/client-photo-update/${token}/acknowledge`, { method: "POST" });
      if (res.ok) await load();
      else {
        const e = await res.json().catch(() => ({}));
        alert(e.detail || "Could not acknowledge");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-photo-error">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-slate-800">Link unavailable</p>
          <p className="text-slate-500 mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const img = (id, thumb = true) => `${API}/public/client-photo-update/${token}/photo/${id}${thumb ? "?thumb=true" : ""}`;
  const dateLabel = new Date(d.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-photo-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png?v=3" alt="SiteProApp" className="w-9 h-9 object-contain" />
          <div>
            <h1 className="font-bold text-slate-900">Site Photos — {dateLabel}</h1>
            <p className="text-sm text-slate-500">{d.site_address || d.site_name}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4">
          {d.photos.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No photos in this update.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {d.photos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setLightbox(p.id)}
                  className="aspect-square rounded-lg overflow-hidden bg-slate-100"
                  data-testid={`public-photo-${p.id}`}
                >
                  <img src={img(p.id)} alt={p.area_tag || ""} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          {d.acknowledged ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-3" data-testid="public-photo-acknowledged">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">Acknowledged</p>
                <p className="text-sm text-green-700">{d.acknowledged_at ? new Date(d.acknowledged_at).toLocaleString("en-AU") : ""}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border p-5">
              <p className="text-sm text-slate-600 mb-4">
                By tapping Acknowledge, you confirm you have seen these site photos from your builder.
              </p>
              <Button onClick={acknowledge} disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 h-12 text-base" data-testid="public-photo-ack-btn">
                {submitting ? "Submitting..." : "Acknowledge"}
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">Sent via SiteProApp</p>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="public-photo-lightbox">
          <img src={img(lightbox, false)} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
